/**
 * What a reaction bar SHOWS, and what one tap does to it.
 *
 * Both apps render their own React bar — `shared/` is framework-free and cross-app
 * imports are ESLint-blocked — but they had drifted on the two decisions that actually
 * matter, and drifting again once the bar is on a feed would mean the same post behaving
 * differently on two screens. Those decisions live here, spec'd, and each app's component
 * keeps only its wiring.
 */

export interface ReactionCount {
  emoji: string;
  count: number;
  /** Whether the reader is one of them, so the chip can render pressed. */
  hasViewerReacted: boolean;
}

/** Praise, surprise and attention, without two that mean the same thing. */
export const DEFAULT_REACTION_EMOJIS = ['❤️', '🔥', '😮', '👏', '🙌', '👀'] as const;

/** How many chips a bar shows before the rest are dropped. */
export const REACTION_BAR_CAP = 6;

/**
 * The chips to render, in the order to render them.
 *
 * PALETTE ORDER IS FIXED, and that is the point (found on a real iPhone, 2026-09-15).
 * Ordering by count instead re-sorts the row the instant you tap: the chip you just hit
 * jumps to the front and the neighbours slide under your thumb, so a second tap lands on
 * an emoji you did not choose. Palette emoji therefore hold their seats whatever their
 * counts; anything people picked from outside the palette still shows, appended after, so
 * a busy post keeps saying what people actually said.
 *
 * When the row would overrun `cap`, the seats nobody has used yield first — an unused
 * palette emoji is an offer, and a chosen one is a fact. Once every seat is taken there
 * are no unused ones left to yield, so the row is simply cut at `cap` rather than being
 * allowed to wrap onto a second line, which a narrow phone card cannot afford.
 */
export function orderSeats(
  counts: readonly ReactionCount[],
  palette: readonly string[],
  cap: number = REACTION_BAR_CAP
): ReactionCount[] {
  const byEmoji = new Map(counts.map((entry) => [entry.emoji, entry]));
  const seats = palette.map((emoji) => byEmoji.get(emoji) ?? { emoji, count: 0, hasViewerReacted: false });
  const extras = counts.filter((entry) => entry.count > 0 && !palette.includes(entry.emoji));

  const overflow = seats.length + extras.length - cap;
  const unusedIndexes = seats.flatMap((entry, index) => (entry.count > 0 ? [] : [index]));
  const dropped = new Set(overflow > 0 ? unusedIndexes.slice(-overflow) : []);

  return [...seats.filter((_, index) => !dropped.has(index)), ...extras].slice(0, cap);
}

/**
 * One tap, applied optimistically.
 *
 * Each chip is an INDEPENDENT toggle — this is not a radio group. Reactions stack, so a
 * post can be several things at once and every extra tap is another signal reaching the
 * author. That is the point of the feature, and the reason only the tapped emoji moves.
 *
 * Returns a new array; the caller keeps the previous one to roll back to, because a chip
 * that lies is worse than a chip that waited.
 */
export function applyToggle(counts: readonly ReactionCount[], emoji: string): ReactionCount[] {
  const existing = counts.find((entry) => entry.emoji === emoji);
  if (!existing) return [...counts, { emoji, count: 1, hasViewerReacted: true }];

  return counts.map((entry) =>
    entry.emoji === emoji
      ? {
          ...entry,
          count: entry.count + (entry.hasViewerReacted ? -1 : 1),
          hasViewerReacted: !entry.hasViewerReacted,
        }
      : entry
  );
}

/** Whether the reader already holds this emoji — read BEFORE `applyToggle` rewrites it. */
export function holdsReaction(counts: readonly ReactionCount[], emoji: string): boolean {
  return counts.find((entry) => entry.emoji === emoji)?.hasViewerReacted ?? false;
}

/**
 * The latest server counts for one target, published by whichever bar just toggled.
 *
 * Two bars share a target — the feed card stays mounted behind the post overlay, and
 * a notification row stays mounted behind its modal. Apollo's cache watch does not
 * deliver `writeQuery` to that still-mounted bar (a fresh mount reads the write; the
 * one already on screen does not), so the snapshot has to move through a channel both
 * instances actually share. Keyed by target, and replaced whole on every publish.
 * The map lives for the life of the JS heap, so a soft sign-in must drop it —
 * see `clearReactionCounts`.
 */
const reactionCounts = new Map<string, ReactionCount[]>();
const reactionCountListeners = new Set<() => void>();
let reactionCountsEpoch = 0;

function reactionCountsKey(targetType: string, targetId: string): string {
  return `${targetType}\0${targetId}`;
}

export function publishReactionCounts(targetType: string, targetId: string, counts: readonly ReactionCount[]): void {
  reactionCounts.set(reactionCountsKey(targetType, targetId), counts.slice());
  reactionCountListeners.forEach((listener) => listener());
}

/**
 * Forget every published snapshot.
 *
 * `hasViewerReacted` belongs to whoever was signed in when the bar last wrote.
 * Sign-in and sign-out do not reload the page, and a remount's ignored snapshot
 * starts out undefined, so `selectReactionCounts` would keep the previous
 * member's pressed chips in front of a refetch. The epoch bumps even when the
 * map was empty, so a bar still holding its own `local` reply can drop it.
 * Call this in the same hand-off that drops the Apollo store.
 */
export function clearReactionCounts(): void {
  reactionCountsEpoch += 1;
  reactionCounts.clear();
  reactionCountListeners.forEach((listener) => listener());
}

/** Bumps on `clearReactionCounts`, including when the map was already empty. */
export function readReactionCountsEpoch(): number {
  return reactionCountsEpoch;
}

export function readReactionCounts(targetType: string, targetId: string): ReactionCount[] | undefined {
  return reactionCounts.get(reactionCountsKey(targetType, targetId));
}

export function subscribeReactionCounts(listener: () => void): () => void {
  reactionCountListeners.add(listener);
  return () => {
    reactionCountListeners.delete(listener);
  };
}

/**
 * Which counts a bar renders.
 *
 * `local` is this bar's own optimistic guess. It has to keep winning until a
 * *different* snapshot arrives: the cache value visible at the moment a tap settles
 * is still the pre-tap one — the watch notifies on a later tick — and painting that
 * is an un-react that looks like it never happened.
 *
 * A different snapshot is the server payload from whichever bar on this target wrote
 * last. Leaving `local` in front of it is how the card behind the overlay keeps its
 * own copy, and the next tap then undoes the server, because `toggleReaction` is a
 * toggle.
 */
export function selectReactionCounts(
  snapshot: readonly ReactionCount[] | undefined,
  ignoredSnapshot: readonly ReactionCount[] | undefined,
  local: readonly ReactionCount[] | null,
  cached: readonly ReactionCount[] | undefined,
  initial: readonly ReactionCount[]
): readonly ReactionCount[] {
  const followed = snapshot !== ignoredSnapshot ? snapshot : local;
  return followed ?? snapshot ?? cached ?? initial;
}
